import React, { Component } from 'react';
import { StyleSheet, css } from 'aphrodite';
import { AppTheme, AppStyles } from '../../styles/AppTheme';
import { Checkbox, FormControl, Input, TextField, Tooltip, MenuItem, Select, InputAdornment, Chip } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { CustomSearchBarStyles } from '../../styles/CustomStyles';
import { AppConfig } from '../../AppConfig';
import { AppButton } from './AppButton';
import { Helper } from '../helpers/Helper';
import { FilterHelper } from '@slx/slixta-common';
import moment from 'moment-timezone'

function generateNumberRangeValue({ startValue, endValue }) {
  if (startValue && endValue) {
    return `ranging from ${startValue} to ${endValue}`
  }
  if (startValue) {
    return `greater than or equal to ${startValue}`
  }
  if (endValue) {
    return `less than or equal to ${endValue}`
  }
  return ''
}

function _isAnyFilterSelected(searchFilters) {
  if (!searchFilters) return false;
  const excludedKeys = ['_sortKey']
  return Object.keys(searchFilters)
    .filter(key => !excludedKeys.includes(key))
    .some(key => {
      const value = searchFilters[key]
      return Array.isArray(value) ? value.length > 0 : !!value
    })
}

export class SearchBar extends Component {
  constructor(props) {
    super(props)
    this.state = {
      searchText: this.props.searchText || '',
      searchFilters: this.props.searchFilters || {},
      filterOptions: this.props.filterOptions || [],
      isAnyFilterSelected: _isAnyFilterSelected(this.props.searchFilters),
      isFilterChanged: false,
      isSearchTextChanged: false
    }
    this.isMobileView = Helper.isMobileView()
    this.debouncedSearch = this.props.autoRefresh && Helper.debounce(this.onSearch, this.props.debounceDuration || 1000).bind(this);
  }

  componentDidUpdate(prevProps, prevState) {
    const isSearchTextChanged = prevState.searchText !== this.state.searchText
    const isSearchFilterChanged = JSON.stringify(prevState.searchFilters) !== JSON.stringify(this.state.searchFilters)
    const isStateChanged = isSearchTextChanged || isSearchFilterChanged

    if (isStateChanged) {
      const isActualChange = (JSON.stringify(this.state.searchFilters) !== JSON.stringify(this.props.searchFilters)) || (this.state.searchText !== (this.props.searchText || ''))
      this.setState({
        isFilterChanged: isActualChange,
        isSearchTextChanged,
        isAnyFilterSelected: _isAnyFilterSelected(this.state.searchFilters)
      })
    }

    if (JSON.stringify(prevProps.searchFilters) !== JSON.stringify(this.props.searchFilters)) {
      this.setState({
        searchFilters: this.props.searchFilters || {},
        isAnyFilterSelected: _isAnyFilterSelected(this.props.searchFilters)
      })
    }

    if (prevProps.searchText !== this.props.searchText) {
      this.setState({ searchText: this.props.searchText || '' })
    }
  }

  handleFilterOperatorChange = (event, current) => {
    this.setState(prevState => {
      const newState = JSON.parse(JSON.stringify(prevState));
      const filterOption = newState.filterOptions.find(fo => fo.key === current.key)
      if (filterOption) {
        filterOption.selectedOperator = event.target.value
      }
      if (this.props.getUpdatedSearchFilters) {
        newState.searchFilters = this.props.getUpdatedSearchFilters(newState.filterOptions, prevState.searchFilters)
      }
      return newState;
    },() => {
      this.props.autoRefresh && this.onSearch()
    })
  }

  resolveFilterChipOptionLabel(filter, key, val) {
    const option = filter && Array.isArray(filter.options)
      ? filter.options.find((item) => item && item.key === val)
      : null
    if (option && (option.label || option.text)) {
      return option.label || option.text
    }
    const baseKey = FilterHelper.getFilterBaseKey(key)
    if (val && ['assignee', 'routedAssignee'].includes(baseKey)) {
      return '[Deleted User]'
    }
    return val
  }

  generateFilterChipLabel(chipLabel,key,value) {
    let keyPart = ''
    if(key === '_datetimezone') {
      keyPart = 'CRM Timezone is '
    } else if(key === 'slxAdId') {
      keyPart = (this.state.searchFilters && this.state.searchFilters._labelForSlxAdId) ? 'Ad is ' : 'Ad ID is '
    } else if(key === 'utmId') {
      keyPart = (this.state.searchFilters && this.state.searchFilters._labelForUtmId) ? 'Campaign is ' : 'Campaign ID is '
    } else if(key === '_sortKey') {
      keyPart = 'Sorted by '
    } else if(key.endsWith('*')) {
      keyPart = <span>{chipLabel + ' contains '} </span>
    } else {
      keyPart = key.endsWith('!') ? <span>{chipLabel + ' is not '} </span> : <span>{chipLabel +  ' is '}</span>
    }

    let filterValue = value
    if(!value ||  (Array.isArray(value) && value.length === 0)) {
      if(key && this.state.searchFilters && Object.keys(this.state.searchFilters).includes(key)) {
        filterValue = this.state.searchFilters[key]
      }
    }
    let valuePart
    let timezone = this.props.timezone || Helper.getLocalTimezone()
    if(value && typeof value === 'object' && value.type === 'dateRange') {
      timezone = ['date_yyyymmdd','dateTime'].includes(value.subType) ? Helper.getLocalTimezone() : timezone
      let dateValue = filterValue.id
      if(dateValue === 'custom') {
        if(filterValue.startValue && filterValue.endValue) {
          dateValue = `between ${moment.tz(filterValue.startValue, timezone).startOf('day').format('Do MMM YYYY')} and ${moment.tz(filterValue.endValue, timezone).endOf('day').format('Do MMM YYYY')}`
        } else if(filterValue.startValue) {
          dateValue = `from ${moment.tz(filterValue.startValue, timezone).startOf('day').format('Do MMM YYYY')}`
        } else {
          dateValue = `until ${moment.tz(filterValue.endValue, timezone).endOf('day').format('Do MMM YYYY')}`
        }
      }
      valuePart = <span className={css(Styles.chipTextValue)}>{this.props.getLabelFromDateId ? (this.props.getLabelFromDateId(dateValue) || dateValue) : dateValue}</span>
    } else if(key === 'slxAdId' && this.state.searchFilters && this.state.searchFilters._labelForSlxAdId) {
      valuePart = <span className={css(Styles.chipTextValue)}>{this.state.searchFilters._labelForSlxAdId}</span>
    } else if(key === 'utmId' && this.state.searchFilters && this.state.searchFilters._labelForUtmId) {
      valuePart = <span className={css(Styles.chipTextValue)}>{this.state.searchFilters._labelForUtmId}</span>
    } else if(key === '_sortKey') {
      let labelForSortKey = [].concat(filterValue)
      if(labelForSortKey.length === 1 && ['lastIntSentTs', 'activeIntSentTs'].includes(labelForSortKey[0])) {
        labelForSortKey = 'Latest Activity'
      }
      valuePart = <span className={css(Styles.chipTextValue)}>{labelForSortKey}</span>
    } else {
      if(value && typeof value === 'object' && value.type === 'numberRange') {
        valuePart = <span className={css(Styles.chipTextValue)}>{generateNumberRangeValue(value)}</span>
      } else {
        valuePart = Array.isArray(filterValue) ? <span>
          {filterValue.map((item, index) => (
            <span key={index}>
              <span className={css(Styles.chipTextValue)}>{item}</span>
              {index < filterValue.length - 1 && (key.endsWith('~') ? ' and ' : ' or ')}
            </span>
          ))}
        </span> : <span className={css(Styles.chipTextValue)}>{filterValue}</span>
      }
    }

    const label = <span className={css(Styles.chipContainer)}>{keyPart}{valuePart}</span>
    return label
  }


  removeAppliedFilter(key) {
    const newSerachFilters = { ...this.state.searchFilters }
    if(key === 'utmId') {
      delete newSerachFilters._labelForUtmId
    } else if(key === 'slxAdId') {
      delete newSerachFilters._labelForSlxAdId
    } else if(key === '_sortKey') {
      delete newSerachFilters._sortKey
    }
    delete newSerachFilters[key];

    let filterOptions = this.state.filterOptions
    const matchedFilter = filterOptions.find(option => option.key === key)
      || filterOptions.find(option => option.key === FilterHelper.getFilterBaseKey(key))
    filterOptions = filterOptions.map((option)=>{
      if(matchedFilter && option.key === matchedFilter.key) {
        delete option.selectedOperator
      }
      return option
    })

    if(this.props.onAppliedFilterRemoved) {
      this.props.onAppliedFilterRemoved(key)
    }

    this.props.onSearch(this.state.searchText || '', newSerachFilters || {}, filterOptions || [])

  }

  displayAppliedFilters() {
    if(!this.state.searchFilters || Object.keys(this.state.searchFilters).length === 0) {
      return(<div className={css(Styles.appliedFiltersContainer)}></div>)
    }

    const searchFilterActions = this.props.searchFilterActions || []

    return(<div className={css(Styles.appliedFiltersContainer)}>
      {
        Object.entries(this.state.searchFilters).map(([key,value])=>{
          const excludedChips = ['_labelForSlxAdId','_labelForUtmId', '_sortKey','_activeTab','_sortOrder']
          if(!excludedChips.includes(key)) {
            const filter = this.state.filterOptions.find((item) => item.key === key)
              || this.state.filterOptions.find((item) => item.key === FilterHelper.getFilterBaseKey(key))
            const chipLabel = FilterHelper.getChipLabelForFilterKey(key, this.state.filterOptions)

            let filterValue = value
            if(filter && filter.options) {
              if(Array.isArray(value)) {
                filterValue = value.map(val => this.resolveFilterChipOptionLabel(filter, key, val)).filter(a => !!a)
              } else {
                filterValue = this.resolveFilterChipOptionLabel(filter, key, value)
              }
            }

            return(
              <div>
                <Chip
                  label={this.generateFilterChipLabel(chipLabel,key,filterValue)}
                  color={AppTheme.secondaryColor}
                  size='medium'
                  onDelete={()=>this.removeAppliedFilter(key)}
                  classes={{ root: css(Styles.chipRoot) }}
                />
              </div>
            )
          }
        }).filter(a => !!a)
      }
      {searchFilterActions.length > 0 && (
        <div className={css(Styles.searchFilterActionsRow)}>
          {searchFilterActions.map((sfa, index) => (
            <AppButton
              key={sfa.label || `search-filter-action-${index}`}
              onClick={() => sfa.onClick && sfa.onClick()}
              buttonText={sfa.label}
              buttonStyle={sfa.buttonStyle || Styles.searchFilterActionButton}
              buttonTextStyle={sfa.buttonTextStyle || Styles.buttonText}
            />
          ))}
        </div>
      )}
    </div>)
  }

  render() {
    const hasSearchActions = this.props.onCustomFiltersBtnClick || this.props.onFiltersBtnClick || this.props.onList || this.props.onClearSearch || this.props.searchBarOptions
    return (
      <div className={css(Styles.container)}>
        <form key='form' autoComplete='off' className={css(Styles.form)}>
          {!this.props.hideFiltersContainer && !this.props.isQuickFilterDisabled && this.state.filterOptions && <div className={css(Styles.filtersContainer)}>
            {this.state.filterOptions.slice(0, this.props.maxFilters || 4).map(filterOption => {
              if (!filterOption) return <></>
              const value = this.processFilterValueToPreFill(filterOption)
              return <FormControl className={css(Styles.formControlMainFilters)} fullWidth>
                <div className={css(Styles.labelContainer)}>
                  <div className={css(filterOption.tip ? Styles.labelTooltipInnerContainer : Styles.labelInnerContainer)}>
                    <p className={css(Styles.label)}>{filterOption.label}</p>
                    {filterOption.tip &&
                      <Tooltip title={filterOption.tip} classes={{ popper: css(Styles.tooltip) }}>
                        <i className='material-icons-outlined' style={{ fontSize: 14, color: '#7d7d7d', cursor: 'pointer', marginLeft: 5, marginTop: 0, alignSelf: 'center' }}>info</i>
                      </Tooltip>
                    }
                    {filterOption.operators && (<Select
                      value={filterOption.selectedOperator || filterOption.defaultOperator}
                      onChange={(event)=>this.handleFilterOperatorChange(event,filterOption)}
                      disableUnderline
                      MenuProps={{
                        getContentAnchorEl: null,
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'center',
                        },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'center',
                        },
                        classes: { paper: css(Styles.isEqualSelectMenu) },
                      }}
                      classes={{ select: css(Styles.isEqualSelection) }}
                      className={css(Styles.isEqualSelect)}
                    >
                      {filterOption.operators.map((option) => (
                        <MenuItem key={option.key.toString()} value={option.label.toString()}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>)}
                  </div>
                </div>
                <Autocomplete
                  value={value}
                  fullWidth={true}
                  disableCloseOnSelect={((this.props.autoRefresh) && (filterOption.multiSelect || filterOption.type === 'check_box'))}
                  multiple={filterOption.multiSelect || filterOption.type === 'check_box'}
                  options={filterOption.options}
                  onChange={(e, val) => this.onAutoCompleteChange(val, filterOption)}
                  getOptionLabel={(option) => option.label || option.text}
                  getOptionSelected={(option, value) => option.key === value.key}
                  ChipProps={{
                    deleteIcon: <svg className={css(Styles.modalCloseIcon)} viewBox='0 0 36 36'>
                      <path d='M28.5 9.62L26.38 7.5 18 15.88 9.62 7.5 7.5 9.62 15.88 18 7.5 26.38l2.12 2.12L18 20.12l8.38 8.38 2.12-2.12L20.12 18z'></path>
                    </svg>
                  }}
                  classes={{
                    tag: css(Styles.tag),
                    popupIndicator: css(Styles.popupIndicator),
                    popper: css(Styles.dialog)
                  }}
                  {...((this.props.autoRefresh) && (filterOption.multiSelect || filterOption.type === 'check_box') && { renderOption: (option, props) => (
                    <>
                      <Checkbox
                        color='primary'
                        checked={props.selected}
                      />
                      {option.label || option.text}
                    </>
                  ) })}
                  renderInput={params =>
                    <TextField
                      {...params}
                      variant={'standard'}
                      className={css(Styles.formControlFilters)}
                      InputProps={{
                        ...params.InputProps,
                        disableUnderline: true,
                        classes: {
                          root: css([Styles.textInputStyles, Styles.autoCompleteInputRoot]),
                          input: css(Styles.formInputStyle, Styles.textInput),
                        },
                      }} />}
                />
              </FormControl>
            })}
          </div>}
          <div className={css(Styles.innerContainer)}>
            <div className={css(Styles.searchInnerContainer)}>
              <div className={css(Styles.searchContainer)}>
                <FormControl key='searchKeywords' margin='normal' fullWidth className={css(Styles.formControl)}>
                  <Input className={css(Styles.searchInput)} type='text' disableUnderline={true}
                    autoFocus={this.props.autoFocus || false}
                    onKeyPress={(e) => { if (e.key === 'Enter') this.onSearch() }}
                    placeholder={this.props.hint || 'Type keywords'} value={this.state.searchText} onChange={this.onSearchKeywordsChange.bind(this)}
                    endAdornment={
                      <InputAdornment position='end' className={css(Styles.searchEndAdornment)}>
                        { !this.props.autoRefresh &&
                          <AppButton
                            buttonStyle={this.state.isFilterChanged ? Styles.buttonSearchEnabled : Styles.buttonSearchDisabled}
                            buttonTextStyle={this.state.isFilterChanged ? Styles.buttonTextEnabled : Styles.buttonTextDisabled}
                            onClick={this.onSearch.bind(this)}
                            iconContainerStyle={this.state.isFilterChanged ? [Styles.selectedIconContainerStyle,Styles.selectedSearchIconContainerStyle] : Styles.iconContainerStyle}
                            iconStyle={Styles.iconStyle}
                            icon= 'search'
                            disabled = {!this.state.isFilterChanged}
                          />
                        }
                      </InputAdornment>
                    }
                  />
                </FormControl>
              </div>
              {hasSearchActions && <div className={css(Styles.buttonInnerContainer)}>
                {this.props.onCustomFiltersBtnClick &&
                <Tooltip title={'Quick Filters'} classes={{ popper: css(Styles.tooltip) }}>
                  <div className={css(Styles.buttonFiltersInnerContainer)}>
                    <AppButton
                      buttonStyle={Styles.filtersButton}
                      buttonTextStyle={Styles.buttonText}
                      iconContainerStyle={Styles.iconContainerStyle}
                      iconStyle={Styles.iconStyle}
                      onClick={this.onCustomFiltersBtnClick.bind(this)}
                      icon= 'filter_list'
                    />
                  </div>
                </Tooltip>}
                {this.props.onFiltersBtnClick &&
               <Tooltip title={'All Filters'} classes={{ popper: css(Styles.tooltip) }}>
                 <div className={css(Styles.buttonFiltersInnerContainer)}>
                   <AppButton
                     buttonStyle={this.state.isAnyFilterSelected ? Styles.filtersButtonSelected : Styles.filtersButton}
                     buttonTextStyle={Styles.buttonText}
                     iconContainerStyle={this.state.isAnyFilterSelected ? Styles.selectedIconContainerStyle : Styles.iconContainerStyle}
                     iconStyle={Styles.iconStyle}
                     onClick={this.onFiltersBtnClick.bind(this)}
                     icon= 'filter_alt'
                   />
                 </div>
               </Tooltip>}
                {this.props.onList &&
                <AppButton
                  buttonStyle={Styles.button}
                  buttonTextStyle={Styles.buttonText}
                  onClick={this.props.onList.bind(this)}
                  buttonText={!this.isMobileView && (this.props.listButtonLabel || 'Show recent')}
                  iconContainerStyle={Styles.iconContainerStyle}
                  iconStyle={Styles.iconStyle}
                  icon= {this.isMobileView && 'clear'} />
                }
                {this.props.onClearSearch &&
                <AppButton
                  buttonStyle={Styles.button}
                  buttonTextStyle={Styles.buttonText}
                  onClick={()=>{
                    this.setState({searchText:''})
                    this.props.onClearSearch()
                  }}
                  buttonText={this.props.clearSearchLabel || 'Clear'} />
                }
                {this.props.searchBarOptions && this.props.searchBarOptions()}
              </div>}
            </div>
          </div>
        </form>
        {this.props.isAppliedFiltersVisible && this.displayAppliedFilters()}
      </div>
    )
  }

  onSearchKeywordsChange = (event) => {
    const target = event.target;
    const value = target.value;
    this.setState({
      searchText: value
    }, () => {
      this.props.autoRefresh && this.debouncedSearch();
    })
  }

  onSearch() {
    this.props.onSearch(this.state.searchText || '', this.state.searchFilters || {}, this.state.filterOptions || [])
  }

  onFiltersBtnClick() {
    this.setState({
      searchText: this.state.searchText || '',
      searchFilters: this.state.searchFilters || {},
      filterOptions: this.state.filterOptions || [],
      isFilterChanged: this.state.isFilterChanged || false,
      isSearchTextChanged: this.state.isSearchTextChanged || false,
    })
    this.props.onFiltersBtnClick(this.state.searchText || '', this.state.searchFilters || {}, this.state.filterOptions || [], this.state.isFilterChanged || false, this.state.isSearchTextChanged || false)
  }

  onCustomFiltersBtnClick() {
    this.props.onCustomFiltersBtnClick()
  }

  onAutoCompleteChange = (value, current) => {
    if (!(current.multiSelect || current.type === 'check_box')) {
      this.setState(prevState => {
        const newState = JSON.parse(JSON.stringify(prevState))
        newState.searchFilters[this.props.getTransformedKey(current)] = (value && value.key) || ''
        return newState
      },() => {
        this.props.autoRefresh && this.onSearch()
      })
    } else if (value) {
      const valDictByKey = {}
      const vals = []
      for (const v of value) {
        if (!valDictByKey[v.key]) {
          valDictByKey[v.key] = true
          vals.push(v.key)
        }
      }
      this.setState(prevState => {
        const newState = JSON.parse(JSON.stringify(prevState))
        newState.searchFilters[this.props.getTransformedKey(current)] = vals
        return newState
      },()=>{
        this.props.autoRefresh && this.debouncedSearch()
      })

    }
  }

  processFilterValueToPreFill = (current) => {
    let value
    const searchFilterKey = FilterHelper.resolveSearchFilterKey(current.key, this.state.searchFilters, this.state.filterOptions)
    const currentFilter = searchFilterKey ? this.state.searchFilters[searchFilterKey] : null
    if (!currentFilter || !current.options) return []
    if (current.multiSelect || current.type === 'check_box') {
      value = []
      for (const o of current.options) {
        if (currentFilter.includes(o.key)) {
          value.push({
            key: o.key,
            text: o.label || o.text
          })
        }
      }
    } else {
      for (const o of current.options) {
        if (o.key === currentFilter || (Array.isArray(currentFilter) && currentFilter.includes(o.key))) {
          value = {
            key: o.key,
            text: o.label || o.text
          }
        }
      }
    }
    return value
  }
}


const Styles = AppConfig.CUSTOM_STYLE ? CustomSearchBarStyles : StyleSheet.create({
  container: {
    minHeight: AppTheme.toolbarHeight * 2 / 3,
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    marginBottom: 16,
    maxWidth: AppTheme.viewPort,
  },
  searchInput: {
    minWidth: 150,
    padding: 10,
    paddingLeft: 22,
    border: '1px solid #E5E5E5',
    textDecoration: 'none',
    borderRadius: AppTheme.borderRadius,
    backgroundColor: 'white'
  },
  button: {
    color: AppTheme.primaryColor,
    border: '1px solid #E5E5E5',
    backgroundColor: AppTheme.secondaryBg
  },
  formControl: {
    margin: 0,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    '@media (max-width: 320px)': {
      maxWidth: 300,
    },
    maxWidth: AppTheme.viewPort,
    width: '100%',
  },
  rightGridPadding: {
    paddingLeft: 19,
    display: 'flex',
    alignItems: 'center',
    '@media (max-width: 767px)': {
      paddingLeft: 0,
    }
  },
  innerContainer: {
    display: 'flex',
    '@media (max-width: 767px)': {
      flexDirection: 'column'
    }
  },
  searchContainer: {
    flex: 1
  },
  buttonInnerContainer: {
    paddingLeft: 10,
    minWidth: 130,
    '@media (max-width: 767px)': {
      flex: 1,
      paddingLeft: 0
    }
  },
  buttonText: {
    color: AppTheme.primaryColor,
    fontWeight: 600
  },
  filtersContainer: {
    display: 'flex',
    gap: '16px',
    marginBottom: '16px',
    '@media (max-width: 767px)': {
      gap: '4px',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
    },
  },
  formControlMain: {
    marginTop: 0,
    marginBottom: 0
  },
  labelContainer: {
    display: 'flex',
    marginBottom: 8
  },
  label: {
    fontSize: 14,
    color: 'grey',
    margin: 0,
    // marginBottom: 8,
    display: 'flex',
  },
  tooltip: {
    zIndex: 9999999999
  },
  modalCloseIcon: {
    width: 12,
    height: 12,
    padding: 1,
    borderRadius: 6
  },
  tag: {
    backgroundColor: AppTheme.secondaryColor,
    color: '#222222',
    fontSize: 13,
    borderRadius: 8,
    height: 26
  },
  popupIndicator: {
    marginRight: 5,
  },
  dialog: {
    zIndex: 130000,
    fontSize: 16,
    color: '#222222',
  },
  formControlMainFilters: {
    marginTop: 16,
    '@media (max-width: 767px)': {
      width: '48%',
    },
  },
  formControlFilters: {
    borderRadius: AppTheme.borderRadius,
    border: '1px solid #E5E5E5',
    overflow: 'hidden'
  },
  textInputStyles: {
    padding: '11px 20px',
    flex: 1,
    textAlign: 'left',
    color: '#222222',
    fontSize: 16
  },
  autoCompleteInputRoot: {
    paddingRight: 56
  },
  formInputStyle: {
    '::-webkit-scrollbar': {
      display: 'none'
    }
  },
  textInput: {
    padding: '4px 0px'
  },
})
